import React, { FC, useRef } from 'react';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { useOptimizedImages } from 'common/hooks/use-optimized-images';
import { picScrollerMouseMove } from 'utils/picture-zoom';
import { cx } from 'linaria';
import { ImagePlaceholder } from '@navi-toolbox/ui-components';
import { mediaDesktop, mediaMobile, mediaMoreThenMobile } from '../styles';
import { pictureContainer, picture, img, zoomCursor } from './styles';
import { useTranslation } from 'react-i18next';

export const DATA_QA_PREVIEW_PICTURE = 'product-picture';
export const DATA_QA_PREVIEW_PICTURE_ELEMENT = 'product-picture-element';
export const DATA_QA_MEDIA_PREVIEW = 'media-preview';

type Props = {
  alt: string;
  src: string;
  onMediaLoad?: (mediaType: string, imgSrc: string, loadTime?: number) => void;
  zoomOnHover?: boolean;
  className?: string;
  size?: number;
  fetchpriority?: 'high' | 'low' | 'auto';
};

export const Picture: FC<Props> = ({
  alt,
  src,
  onMediaLoad,
  zoomOnHover = true,
  className,
  size,
  fetchpriority,
}) => {
  const { t } = useTranslation();
  const { desktop, largeDesktop } = useBreakpoints() || {
    desktop: true,
    largeDesktop: true,
  };

  const isZoomOnHoverEnabled = zoomOnHover && (desktop || largeDesktop);

  const ref = useRef<HTMLElement | null>(null);

  const imgSizes = {
    mobile: {
      w: '400',
      h: '400',
    },
    tablet: {
      w: '574',
      h: '468',
    },
    desktop: {
      w: '1000',
      h: '1000',
    },
  };

  const optimizedImage = useOptimizedImages(src, imgSizes);

  function handleMediaLoad() {
    onMediaLoad?.('mediaPdpPhoto', src);
  }

  function handleMouseLeave() {
    ref.current?.style.setProperty('transform', 'none');
  }

  return (
    <div data-qa={DATA_QA_MEDIA_PREVIEW} className={cx(pictureContainer, className)}>
      <picture ref={ref} className={picture} data-qa={DATA_QA_PREVIEW_PICTURE_ELEMENT}>
        <source
          media={mediaDesktop}
          srcSet={`${optimizedImage.desktop?.xx} 2x, ${optimizedImage.desktop?.x} 1x`}
        />
        <source
          media={mediaMoreThenMobile}
          srcSet={`${optimizedImage.tablet?.xx} 2x, ${optimizedImage.tablet?.x} 1x`}
        />
        <source
          media={mediaMobile}
          srcSet={`${optimizedImage.mobile?.xx} 2x, ${optimizedImage.mobile?.x} 1x`}
        />
        <ImagePlaceholder
          width={size}
          height={size}
          fallbackWidth={size}
          fallbackHeight={size}
          fallbackAlt={t('ui.image.alt')}
          onLoad={handleMediaLoad}
          onMouseMove={isZoomOnHoverEnabled ? picScrollerMouseMove(ref) : () => {}}
          onMouseLeave={handleMouseLeave}
          src={optimizedImage.desktop?.x || ''}
          srcSet={`${optimizedImage.desktop?.xx} 2x, ${optimizedImage.desktop?.x} 1x`}
          alt={alt}
          title={alt}
          className={cx(img, isZoomOnHoverEnabled ? zoomCursor : undefined)}
          data-qa={DATA_QA_PREVIEW_PICTURE}
          fetchPriority={fetchpriority}
        />
      </picture>
    </div>
  );
};
